import React, {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

export type ToastVariant = 'success' | 'error' | 'info';

export type ShowToastOptions = {
  variant?: ToastVariant;
  /** ms, default 4000; use 0 to require manual dismiss (we still auto-remove at 8s for safety) */
  duration?: number;
};

type ToastItem = {
  id: string;
  message: string;
  variant: ToastVariant;
};

type ToastContextValue = {
  showToast: (message: string, options?: ShowToastOptions) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 4000;
const MAX_TOASTS = 4;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const regionId = useId();

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, options?: ShowToastOptions) => {
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      const variant = options?.variant ?? 'info';
      const duration =
        options?.duration === undefined
          ? DEFAULT_DURATION
          : options.duration;

      setToasts((prev) => {
        const next = [...prev, { id, message, variant }];
        if (next.length > MAX_TOASTS) {
          return next.slice(-MAX_TOASTS);
        }
        return next;
      });

      if (duration > 0) {
        window.setTimeout(() => removeToast(id), duration);
      } else {
        window.setTimeout(() => removeToast(id), 8000);
      }
    },
    [removeToast]
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          id={regionId}
          className="pointer-events-none fixed bottom-0 left-0 right-0 z-[10000] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6"
          aria-label="알림"
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              aria-live="polite"
              className={clsx(
                'pointer-events-auto max-w-md rounded-xl px-4 py-3 text-sm font-medium shadow-lg ring-1 ring-black/5 transition-opacity',
                t.variant === 'success' &&
                  'bg-emerald-50 text-emerald-900 ring-emerald-600/15',
                t.variant === 'error' &&
                  'bg-red-50 text-red-900 ring-red-600/15',
                t.variant === 'info' &&
                  'bg-white text-slate-900 ring-slate-900/10'
              )}
            >
              {t.message}
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
